'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { IconHelpHexagonFilled, IconHelpCircle } from '@tabler/icons-react';

import { Container } from '@/components/container';
import { Heading } from '@/components/elements/heading';
import { Subheading } from '@/components/elements/subheading';
import { PageBreadcrumb } from '@/components/page-breadcrumb';
import { jakarta } from '@/lib/fonts';
import { cn } from '@/lib/utils';

export const FAQ = ({
  heading,
  sub_heading,
  faqs,
  locale = 'en',
}: {
  heading: string;
  sub_heading?: string;
  faqs: any[];
  locale?: string;
}) => {
  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-20 md:pb-28 bg-[#f4f2fc] dark:bg-neutral-900/90 border-b border-[#e5e2f5] dark:border-neutral-800/80">
      {/* Discreet background ambient lighting */}
      <div className="absolute inset-0 pointer-events-none -z-10 overflow-hidden">
        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[550px] h-[550px] rounded-full blur-[150px] opacity-[0.04] bg-[#c02029]" />
        <div className="absolute top-1/4 left-0 w-[450px] h-[450px] rounded-full blur-[150px] opacity-[0.035] bg-[#c02029]" />
      </div>

      <Container>
        {/* Streamlined Breadcrumb */}
        <div className="flex justify-center">
          <PageBreadcrumb
            locale={locale}
            center
            items={[{ label: 'FAQ' }]}
          />
        </div>

        <Heading as="h1" className="pt-2">
          {heading}
        </Heading>

        {sub_heading && (
          <Subheading className="max-w-3xl mx-auto">
            {sub_heading}
          </Subheading>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 pt-16 max-w-6xl mx-auto">
          {faqs &&
            faqs.map((faq: { question: string; answer: string }, index: number) => (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                key={faq.question || index}
                className="p-6 sm:p-8 rounded-3xl bg-white dark:bg-neutral-900 border border-[#e5e2f5] dark:border-neutral-800 shadow-xl shadow-[#301754]/5 hover:border-[#c02029]/30 transition-all duration-300 flex flex-col justify-start group"
              >
                <div className="flex items-start gap-4 mb-4">
                  <span className="w-8 h-8 rounded-full bg-[#c02029]/10 text-[#c02029] flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#c02029] group-hover:text-white transition-colors">
                    <IconHelpCircle className="w-4 h-4 stroke-[2.25]" />
                  </span>
                  <h3
                    className={cn(
                      jakarta.className,
                      'text-lg sm:text-xl font-bold text-[#2e1452] dark:text-neutral-100 leading-snug group-hover:text-[#c02029] transition-colors'
                    )}
                  >
                    {faq.question}
                  </h3>
                </div>
                <p className="text-base text-[#5e5873] dark:text-neutral-300 leading-relaxed pl-12">
                  {faq.answer}
                </p>
              </motion.div>
            ))}
        </div>
      </Container>
    </section>
  );
};

export default FAQ;
